import { token } from '@/infrastructure/container';
import type { Court, PlayerNumber, SessionState } from '@/domain/session';

// The facade the front desk store talks to. The real implementation sits over @celeriant/sdk;
// tests hand the store an in-memory one. Pinned by session/contracts/p1-session-roster.md.

export interface RegisterPlayerInput {
  court: Court;
  correlationId: string;
  idempotencyKey: string;
  // The number the desk typed, on a PRE-ALLOCATED day (docs/roster.md). Absent on a sequential one,
  // where the reducer assigns it from fold order. Optional and not required, because P1's contract
  // pinned this interface and its blind oracle ships a fake that implements it.
  number?: PlayerNumber;
}

export interface IFrontDeskGateway {
  // ONE write attempt. Resolves when the server acks AND the registration has folded into
  // sessionState (registration is OCC, and an OCC write response is safe to fold from, so the
  // desk sees its own player the moment this resolves - docs/sync.md). Rejects with ConflictError
  // (from '@/domain/errors') on an OCC clash, ServerError otherwise. The retry ladder is the
  // caller's, not this method's.
  registerPlayer(input: RegisterPlayerInput): Promise<void>;
  readonly sessionState: SessionState; // MobX observable, the current fold
  // The device's own network interface. The fast local signal: false is reliably offline, true is
  // only a hint (docs/sync.md).
  readonly online: boolean; // MobX observable connectivity
  // Whether the GATEWAY is answering, which `navigator.onLine` cannot tell you. Added in P2 from
  // the SDK's status surface (`src/services/GatewayReach.ts`, which owns the clear/latch
  // semantics). OPTIONAL, and it stays optional: P1's contract pinned this interface and its blind
  // oracle ships a fake that implements it, so a required member here would break a test nobody is
  // allowed to edit. A gateway that does not report reachability is read as reachable — the local
  // signal above still gates, which is exactly the P1 behaviour.
  readonly gatewayReachable?: boolean; // MobX observable
}

export const TOKEN_IFrontDeskGateway = token<IFrontDeskGateway>('IFrontDeskGateway');
